import {
	Breadcrumb,
	BreadcrumbItem,
	BreadcrumbLink,
	Button,
	Card,
	CardBody,
	Flex,
	Spinner,
	Tab,
	TabList,
	TabPanel,
	TabPanels,
	Text,
	useDisclosure,
} from "@chakra-ui/react";
import PageTitle from "../components/PageTitle";
import { ChevronRightIcon, MoonIcon } from "@chakra-ui/icons";
import { useParams } from "react-router-dom";
import { useCenter, useCenterStat } from "../hooks/centers.hooks";
import ContactCentersUsersTable from "../components/table/ContactCentersUsersTable";
import CenterModal from "../components/modals/CenterModal";
import Address from "../components/Address";
import AcceptWithReasonModal, {
	ValidationEvent,
} from "../components/modals/AcceptWithReasonModal";
import BuildingIcon from "../components/icons/BuildingIcon";
import { useMutation, useQueryClient } from "react-query";
import { Center } from "../types/center.types";
import { centerToggleSleepMode } from "../mutations/center.mutations";
import MoonSteroidIcon from "../components/icons/MoonSteroidIcon";
import PersistingTabIndex from "../components/PersistingTabIndex";
import { fPhoneNumber } from "../lib/format";

const CenterBadge = ({ center }: { center: Center }) => {
	return (
		<Flex
			w="100%"
			backgroundColor={"gray.100"}
			border="solid 1px"
			borderColor="gray.300"
			borderRadius={"xl"}
			my={4}
			py={2}
			justifyContent={"center"}
		>
			<Flex
				w="48px"
				h="48px"
				bg="brand.700"
				border="solid 1px"
				borderColor="white"
				justifyContent="center"
				alignItems="center"
				borderRadius={100}
				mr="8px"
			>
				<BuildingIcon w="24px" h="24px" color="white"></BuildingIcon>
			</Flex>
			<Flex direction={"column"} justifyContent={"center"}>
				<Text
					textTransform={"capitalize"}
					lineHeight="18px"
					fontSize="14px"
					fontWeight="600"
				>
					{center.name}
				</Text>
				<Text
					lineHeight="15px"
					fontSize="10px"
					fontWeight="600"
					color="gray.500"
				>
					Centre
				</Text>
			</Flex>
		</Flex>
	);
};

function ContactCentersDetailPage() {
	const { id } = useParams();
	const { data: center, isLoading: centerLoading } = useCenter(id);
	const { data: centerStat, isLoading: centerStatLoading } = useCenterStat(id);
	const { isOpen, onOpen, onClose } = useDisclosure();
	const sleepModal = useDisclosure();
	const queryClient = useQueryClient();
	const { mutate: mutateEnterSleepMode } = useMutation(centerToggleSleepMode, {
		onSuccess: () => {
			queryClient.invalidateQueries("center");
		},
	});
	const handleSleepMode = (validation: ValidationEvent) => {
		mutateEnterSleepMode({
			id,
			reason: validation.data.reason,
		});
	};
	const toggleSleepMode = () => {
		mutateEnterSleepMode({
			id,
		});
	};

	if (centerLoading) {
		return <Spinner></Spinner>;
	}
	if (center === null || center == undefined) {
		return <></>;
	}
	return (
		<>
			<Flex justifyContent={"space-between"}>
				<Flex direction={"column"}>
					<Breadcrumb
						spacing="8px"
						separator={<ChevronRightIcon color="gray.500" />}
					>
						<BreadcrumbItem>
							<BreadcrumbLink
								href="/contacts"
								color="gray.600"
								fontWeight={600}
								fontSize="14px"
							>
								Contact
							</BreadcrumbLink>
						</BreadcrumbItem>

						<BreadcrumbItem>
							<BreadcrumbLink
								href={
									center.enterpriseId
										? `/contacts/enterprises/${center.enterpriseId}/`
										: "#"
								}
								color="brand.700"
								fontWeight={600}
								fontSize="14px"
							>
								<Text textTransform={"capitalize"}>
									{center.enterprise?.name ?? "---"}
								</Text>
							</BreadcrumbLink>
						</BreadcrumbItem>

						<BreadcrumbItem>
							<BreadcrumbLink
								href="#"
								color="brand.700"
								fontWeight={600}
								fontSize="14px"
							>
								<Text textTransform={"capitalize"}>{center.name}</Text>
							</BreadcrumbLink>
						</BreadcrumbItem>
					</Breadcrumb>

					<PageTitle>{center.name}</PageTitle>

					{/* STATS */}
					<Flex gap={4} mt="2rem">
						<Flex direction={"column"} w={"10rem"}>
							<Text
								lineHeight="15px"
								fontSize="10px"
								fontWeight="700"
								color="gray.400"
							>
								Commandes
							</Text>
							{!centerStatLoading && (
								<Text
									lineHeight="36px"
									fontSize="24px"
									fontWeight="600"
									color="gray.950"
								>
									{centerStat?.commandsCount ?? "---"}
								</Text>
							)}
							{centerStatLoading && <Spinner></Spinner>}
						</Flex>

						<Flex direction={"column"} w={"10rem"}>
							<Text
								lineHeight="15px"
								fontSize="10px"
								fontWeight="700"
								color="gray.400"
							>
								Utilisateurs
							</Text>
							{!centerStatLoading && (
								<Text
									lineHeight="36px"
									fontSize="24px"
									fontWeight="600"
									color="gray.950"
								>
									{centerStat?.usersCount ?? "---"}
								</Text>
							)}
							{centerStatLoading && <Spinner></Spinner>}
						</Flex>
					</Flex>
				</Flex>
				{center.sleepModeAt !== null && (
					<Flex
						backgroundColor={"brand.50"}
						direction={"column"}
						alignItems={"left"}
						p={4}
						borderRadius="xl"
						border="solid 1px"
						borderColor="gray.300"
						w={"400px"}
					>
						<MoonSteroidIcon h={"38px"} w={"38px"} color={"brand.700"} />

						<Flex direction={"column"} mt={1}>
							<Text
								lineHeight="21px"
								fontSize="14px"
								fontWeight="600"
								color="gray.700"
							>
								Centre en sommeil
							</Text>
							<Text
								lineHeight="21px"
								fontSize="14px"
								fontWeight="400"
								color="gray.600"
							>
								{center.sleepModeReason}
							</Text>
						</Flex>

						<Button
							color={"brand.700"}
							variant="link"
							w="fit-content"
							mt={4}
							onClick={toggleSleepMode}
						>
							Réactiver
						</Button>
					</Flex>
				)}
			</Flex>

			{/* TABS */}
			<PersistingTabIndex id="contact-centers" mt={6}>
				<TabList>
					<Tab _selected={{ color: "brand.700", borderColor: "brand.700" }}>
						Informations
					</Tab>
					<Tab _selected={{ color: "brand.700", borderColor: "brand.700" }}>
						Utilisateurs
					</Tab>
				</TabList>

				<TabPanels>
					<TabPanel px={0}>
						<Card mt={4}>
							<CardBody>
								<Flex
									my="12px"
									mx="4px"
									py="4px"
									borderBottom="1px"
									borderColor="gray.100"
									justifyContent={"space-between"}
									_hover={{ bg: "zinc.50" }}
								>
									<Text>Nom</Text>
									<Text textTransform={"capitalize"}>{center.name}</Text>
								</Flex>

								<Flex
									my="12px"
									mx="4px"
									py="4px"
									borderBottom="1px"
									borderColor="gray.100"
									justifyContent={"space-between"}
									_hover={{ bg: "zinc.50" }}
								>
									<Text>ID code client</Text>
									<Text>{center.sageId}</Text>
								</Flex>

								<Flex
									my="12px"
									mx="4px"
									py="4px"
									borderBottom="1px"
									borderColor="gray.100"
									justifyContent={"space-between"}
									_hover={{ bg: "zinc.50" }}
								>
									<Text>Adresse du centre</Text>
									<Address id={center.shippingAddressId}></Address>
								</Flex>

								<Flex
									my="12px"
									mx="4px"
									py="4px"
									borderBottom="1px"
									borderColor="gray.100"
									justifyContent={"space-between"}
									_hover={{ bg: "zinc.50" }}
								>
									<Text>Téléphone</Text>
									<Text>{fPhoneNumber(center.phoneNumber ?? "")}</Text>
								</Flex>

								<Flex
									my="12px"
									mx="4px"
									py="4px"
									borderBottom="1px"
									borderColor="gray.100"
									justifyContent={"space-between"}
									_hover={{ bg: "zinc.50" }}
								>
									<Text>Email</Text>
									<Text>{center.email}</Text>
								</Flex>

								<Flex w="100%" justifyContent={"end"}>
									<Button onClick={() => onOpen()}>Modifier</Button>
									<CenterModal
										id={center.id}
										isOpen={isOpen}
										onOpen={onOpen}
										onClose={onClose}
									></CenterModal>
								</Flex>
							</CardBody>
						</Card>

						{center.sleepModeAt === null && (
							<Flex mt={4}>
								<Button onClick={sleepModal.onOpen}>Mise en sommeil</Button>
								<AcceptWithReasonModal
									title={"Mise en sommeil"}
									icon={<MoonIcon color="brand.700" />}
									description={<CenterBadge center={center} />}
									discardButtonText="Annuler"
									validationButtonText="Mettre en sommeil"
									colorScheme="brand"
									isOpen={sleepModal.isOpen}
									onClose={sleepModal.onClose}
									onValidation={handleSleepMode}
								></AcceptWithReasonModal>
							</Flex>
						)}
					</TabPanel>

					<TabPanel px={0}>
						<ContactCentersUsersTable id={id}></ContactCentersUsersTable>
					</TabPanel>
				</TabPanels>
			</PersistingTabIndex>
		</>
	);
}

export default ContactCentersDetailPage;
